import { AuthoritiesDocumentDTO, AuthoritiesDocumentDataSource } from 'models/purchaseFlow'
import { ImmutableList, ImmutableMap, Nullable } from 'models/helpers'
import { isLandRegistrationDocumentSelection, useAuthoritiesDocuments, usePurchaseFlowProducts } from '../../_main/contexts'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { GroupedParcels } from 'components/common/ParcelViewerMap'
import { Moment } from 'moment-timezone'
import constate from 'constate'
import { uniqueId } from 'lodash'

/**
 * Represents an item in the land register sheet.
 */
type LandRegisterSheetItem = {
  /** The name of land register sheet item. */
  name: string
  /** The district of the item. */
  district: string
  /** The list of floors in the item. */
  floors: ImmutableList<LandRegisterSheetFloorItem>
}

/**
 * Represents an item in the land register sheet floor.
 */
type LandRegisterSheetFloorItem = {
  /** The hallway of the floor. */
  hallway: string
  /** The parcel of the floor. */
  parcels: ImmutableList<LandRegistrationSheetParcel>
}

/**
 * Represents an item in the land register sheet floor.
 */
type LandRegistrationSheetParcel = {
  /** The number of the parcel */
  number: string,
}

/**
 * Generates a unique ID for a land register sheet.
 * @returns The generated unique ID.
 */
const _generateLandRegisterSheetId = (): string => uniqueId('land-register-')

/**
 * Generates a default land register sheet item.
 * @returns The generated default land register sheet item.
 */
const _generateDefaultLandRegisterSheetItem = (): LandRegisterSheetItem => {
  return {
    name: '',
    district: '',
    floors: ImmutableList([
      {
        hallway: '',
        parcels: ImmutableList([{
          number: '',
        }])
      }
    ])
  }
}

export const [PropertyInformationStepContextProvider, usePropertyInformationStep] = constate(() => {

  const { selectedProducts } = usePurchaseFlowProducts()
  const { searchProduct } = useAuthoritiesDocuments()

  const [landRegisterSheetItems, setLandRegisterSheetItems] = useState<ImmutableMap<string, LandRegisterSheetItem>>(ImmutableMap([]))
  const [landlordFirstName, setLandlordFirstName] = useState<string>('')
  const [landlordLastName, setLandlordLastName] = useState<string>('')
  const [landlordDateOfBirth, setLandlordDateOfBirth] = useState<Nullable<Moment>>(null)
  const [comment, setComment] = useState<string>('')
  const [isEditLandSheetManually, setIsEditLandSheetManually] = useState<boolean>(false)

  const isLandLordLandSheetInformationMandatory = useMemo(
    () => isLandRegistrationDocumentSelection(selectedProducts),
    [selectedProducts]
  )

  /**
   * Updates the land register sheet item with the specified ID.
   * 
   * @param id - The ID of the land register sheet item to update.
   * @param updates - The partial updates to apply to the land register sheet item.
   */
  const updateSheetList = (id: string, updates: Partial<LandRegisterSheetItem>) => {
    if (!landRegisterSheetItems.get(id)) return

    setLandRegisterSheetItems((prevItems) => prevItems.update(id, item => ({ ...item!, ...updates })))
  }

  /**
   * Updates the land register sheet floor with the specified id and floor index.
   * 
   * @param id - The id of the land register sheet.
   * @param floorIndex - The index of the floor to update.
   * @param updates - The partial updates to apply to the floor.
   */
  const updateLandRegisterSheetFloor = (id: string, floorIndex: number, updates: Partial<LandRegisterSheetFloorItem>) => {

    if (!landRegisterSheetItems.get(id)?.floors.get(floorIndex)) return

    updateSheetList(
      id,
      {
        floors: landRegisterSheetItems.get(id)!.floors.update(floorIndex, prev => ({ ...prev!, ...updates }))
      }
    )
  }

  /**
   * Updates the parcel of a land register sheet.
   * 
   * @param sheetId - The ID of the land register sheet.
   * @param floorIndex - The index of the floor.
   * @param parcelIndex - The index of the parcel to update.
   * @param update - The partial updates to apply to the parcel.
   */
  const updateParcel = (sheetId: string, floorIndex: number, parcelIndex: number, update: Partial<LandRegistrationSheetParcel>) => {
    const sheet = landRegisterSheetItems.get(sheetId)
    if (!sheet) return

    const floor = sheet.floors.get(floorIndex)
    if (!floor) return

    updateLandRegisterSheetFloor(
      sheetId,
      floorIndex,
      {
        parcels: floor.parcels.update(parcelIndex, (prev) => ({ ...prev!, ...update })),
      }
    )
  }

  /**
   * Adds a new land register sheet.
   */
  const onAddLandRegisterSheet = () => {
    setLandRegisterSheetItems(prev => prev.set(_generateLandRegisterSheetId(), _generateDefaultLandRegisterSheetItem()))
  }

  /**
   * Removes a land register sheet.
   * @param id - The ID of the land register sheet.
   */
  const onRemoveLandRegisterSheet = (id: string) => {
    setLandRegisterSheetItems(prev => prev.remove(id))
  }

  /**
   * Adds a new floor to a land register sheet.
   * @param id - The ID of the land register sheet.
   */
  const addFloor = (id: string) => {
    if (!landRegisterSheetItems.get(id)) return

    const newFloor = {
      hallway: '',
      parcels: ImmutableList([{
        number: '',
      }]),
    }

    updateSheetList(id, { floors: landRegisterSheetItems.get(id)!.floors.push(newFloor) })
  }

  /**
   * Removes a floor from a land register sheet.
   * @param id - The ID of the land register sheet.
   * @param index - The index of the floor to remove.
   */
  const removeFloor = (id: string, index: number) => {
    if (!landRegisterSheetItems.get(id)) return

    updateSheetList(id, { floors: landRegisterSheetItems.get(id)!.floors.splice(index, 1) })
  }

  /**
   * Adds a new floor to a land register sheet.
   * @param sheetId - The ID of the land register sheet.
   * @param floorIndex - The index of the floor to add parcel
   */
  const addParcel = (sheetId: string, floorIndex: number) => {
    const sheet = landRegisterSheetItems.get(sheetId)
    if (!sheet) return

    const floor = sheet.floors.get(floorIndex)
    if (!floor) return

    updateLandRegisterSheetFloor(
      sheetId,
      floorIndex,
      {
        parcels: floor.parcels.push({
          number: '',
        }),
      }
    )
  }

  /**
   * Removes a floor from a land register sheet.
   * @param sheetId - The ID of the land register sheet.
   * @param floorIndex - The index of the floor.
   * @param parcelIndex - The index of the parcel to remove.
   */
  const removeParcel = (sheetId: string, floorIndex: number, parcelIndex: number) => {
    const sheet = landRegisterSheetItems.get(sheetId)
    if (!sheet) return

    const floor = sheet.floors.get(floorIndex)
    if (!floor) return

    updateLandRegisterSheetFloor(
      sheetId,
      floorIndex,
      {
        parcels: floor.parcels.splice(parcelIndex, 1),
      }
    )
  }

  const isLandRegisterNameValid = (name: string) => {
    // Regex taken from DocEstate API documentation
    const regex = new RegExp(/^[0-9]+[A-Za-z]?$/)

    return !!name.match(regex)
  }

  /**
   * Indicates whether the landlord information is partially filled.
   */
  const isLandlordInfoMissing: boolean = useMemo(() => {
    return isLandLordLandSheetInformationMandatory && (!landlordFirstName || !landlordLastName || !landlordDateOfBirth)
  }, [isLandLordLandSheetInformationMandatory, landlordDateOfBirth, landlordFirstName, landlordLastName])

  useEffect(() => {
    // Set default land register sheet items
    if (!landRegisterSheetItems.size) {
      setLandRegisterSheetItems(prev => prev.set(_generateLandRegisterSheetId(), _generateDefaultLandRegisterSheetItem()))
    }
    // Only run on first render, no need to add dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetLandRegisterSheetItems = useCallback(() => {
    setLandRegisterSheetItems(ImmutableMap([[_generateLandRegisterSheetId(), _generateDefaultLandRegisterSheetItem()]]))
  }, [])

  const getAuthoritiesDocumentRequestData = useCallback((): AuthoritiesDocumentDTO | undefined => {
    const nonSearchProduct = !searchProduct || !selectedProducts[searchProduct.id]
    const landRegistrationSheets = landRegisterSheetItems.toArray().map(([_, value]) => ({
      name: value.name,
      district: value.district,
      floors: value.floors.toArray().map(({ hallway, parcels }) => ({
        hallway,
        parcels: parcels.toArray()
      }))
    }))

    let landLordInformation = undefined

    if (landlordFirstName && landlordLastName && landlordDateOfBirth) {
      landLordInformation = {
        firstName: landlordFirstName,
        lastName: landlordLastName,
        dateOfBirth: landlordDateOfBirth.format('YYYY-MM-DD'),
      }
    }

    return {
      landRegistrationSheets: nonSearchProduct ? landRegistrationSheets : [],
      landLordInformation,
      comment,
      dataSource: !isEditLandSheetManually ? AuthoritiesDocumentDataSource.MAP : undefined,
    }
  }, [isEditLandSheetManually, comment, landRegisterSheetItems, landlordDateOfBirth, landlordFirstName, landlordLastName, searchProduct, selectedProducts])

  /**
   * Updates the land register sheet items by processing the grouped parcels (districts, floor sections and parcels).
   * @param groupedParcels - An object where keys represent districts and values are objects containing floor sections.
   */
  const updateLandRegisterWithParcels = useCallback((groupedParcels: GroupedParcels) => {
    const sheetIds = landRegisterSheetItems.keySeq().toArray()

    if (!groupedParcels || Object.keys(groupedParcels).length === 0) {
      // If groupedParcels is empty, reset the first item to default values but keep its id
      // We dont want to use the default id generation here, because it would create a new id and re-render view
      setLandRegisterSheetItems(prev => {
        if (prev.size === 0) return prev
        const firstEntry = prev.entrySeq().first()
        if (!firstEntry) return prev
        const [firstId] = firstEntry
        return ImmutableMap([
          [firstId, {
            name: '',
            district: '',
            floors: ImmutableList([
              {
                hallway: '',
                parcels: ImmutableList([{ number: '' }])
              }
            ])
          }]
        ])
      })
      return
    }

    let newLandRegisterSheetItems: ImmutableMap<string, LandRegisterSheetItem> = ImmutableMap([])

    // Cache landRegisterSheetItems in a Map for optimized lookups
    const landRegisterSheetItemsMap = new Map(
      landRegisterSheetItems.toArray().map(([id, sheet]) => [sheet.district, { id, sheet }])
    )

    Object.entries(groupedParcels).forEach(([district, floorSections], sheetIndex) => {
      let sheetId: string = sheetIds[sheetIndex]
      let floorsData: ImmutableList<LandRegisterSheetFloorItem> = ImmutableList() // Initialize floorsData for each sheet

      // Check if the district exists in the cached Map
      const existingSheet = landRegisterSheetItemsMap.get(district)?.sheet

      if (!existingSheet && !sheetId) {
        sheetId = _generateLandRegisterSheetId()
      }

      Object.entries(floorSections).forEach(([section, parcels]) => {
        const newFloorData = {
          hallway: section === 'undefined' || section === '-' ? '' : section,
          parcels: ImmutableList(parcels.map(parcel => ({ number: parcel })))
        }
        // Update floorsData by pushing the new floor data
        floorsData = floorsData.push(newFloorData)
      })

      // Merge new floors with existing ones if sheetId already exists
      const existingNewSheet = newLandRegisterSheetItems.get(sheetId)
      const mergedFloors = existingNewSheet?.floors
        ? existingNewSheet.floors.concat(floorsData)
        : floorsData

      // Set the new land register sheet item with the merged floors
      newLandRegisterSheetItems = newLandRegisterSheetItems.set(sheetId, {
        name: existingNewSheet?.name || '',
        district,
        floors: mergedFloors,
      })
    })
    setLandRegisterSheetItems(newLandRegisterSheetItems)
  }, [landRegisterSheetItems, setLandRegisterSheetItems])

  return {
    landRegisterSheetItems,
    isLandlordInfoMissing,
    landlordFirstName,
    landlordLastName,
    landlordDateOfBirth,
    comment,
    addFloor,
    removeFloor,
    addParcel,
    removeParcel,
    onAddLandRegisterSheet,
    onRemoveLandRegisterSheet,
    setLandlordFirstName,
    setLandlordLastName,
    setLandlordDateOfBirth,
    setLandRegisterSheetItems,
    updateSheetList,
    updateLandRegisterSheetFloor,
    updateParcel,
    getAuthoritiesDocumentRequestData,
    setComment,
    updateLandRegisterWithParcels,
    isLandLordLandSheetInformationMandatory,
    resetLandRegisterSheetItems,
    isEditLandSheetManually,
    setIsEditLandSheetManually,
    isLandRegisterNameValid,
  }
})
